/**
 * The "Advanced" part of a connector form (item 65B, 2026-10-30): the
 * technical settings — what a response is trimmed to, pinned and default
 * parameters, the per-turn call limit, citations, caching, the result size,
 * the header a key goes in — sit in a collapsed section, so the form's
 * default view is what most people set: the name, the description, the
 * address, the sign-in, which tools agents get, and the guide.
 *
 * Nothing is removed and nothing is hidden silently: a section whose values
 * differ from the defaults says "Advanced settings in use" while it is
 * closed, and opening it shows every value as stored.
 */

import { useId, useState, type ReactNode } from 'react';
import { useLanguage } from '@hooks/useLanguage';
import type { ApiOperationSettings } from '../services/apiConnectorService';

/** The server's defaults (api_connector.py, agent_tools.py). */
export const DEFAULT_CACHE_TTL_SECONDS = 3600;
export const DEFAULT_MAX_RESULT_CHARS = 6000;

const filled = (value: unknown): boolean => {
  if (value == null) return false;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'object') return Object.keys(value as object).length > 0;
  if (typeof value === 'string') return value.trim().length > 0;
  return true;
};

/** An action's advanced settings differ from the defaults (on/off is not one of them). */
export function operationAdvancedInUse(s: Partial<ApiOperationSettings> | undefined): boolean {
  if (!s) return false;
  return filled(s.fields) || filled(s.verifies) || filled(s.max_calls_per_turn) || filled(s.pinned)
    || filled(s.defaults) || filled(s.description) || filled(s.cache_ttl_seconds);
}

/** An API connector's own advanced settings differ from the defaults. */
export function connectorAdvancedInUse(cacheTtl: number, maxResultChars: number | undefined): boolean {
  return cacheTtl !== DEFAULT_CACHE_TTL_SECONDS
    || (maxResultChars !== undefined && maxResultChars !== DEFAULT_MAX_RESULT_CHARS);
}

export function InUseMark({ testId }: { testId?: string }) {
  const { t } = useLanguage();
  return <span className="cxa__in-use" data-testid={testId}>{t('bots.connectors.advanced.inUse')}</span>;
}

interface SectionProps {
  /** What the section is for, in one plain line (shown when open). */
  lead?: string;
  inUse: boolean;
  testId: string;
  children: ReactNode;
}

/** The connector's own "Advanced" section, closed by default. */
export function AdvancedSection({ lead, inUse, testId, children }: SectionProps) {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const bodyId = useId();
  return (
    <div className={`cxa ${open ? 'is-open' : ''}`} data-testid={testId}>
      <button type="button" className="cxa__toggle" aria-expanded={open} aria-controls={open ? bodyId : undefined}
              onClick={() => setOpen(o => !o)} data-testid={`${testId}-toggle`}>
        <ChevronGlyph />
        <span className="cxa__label">{t('bots.connectors.advanced.title')}</span>
        {inUse && <InUseMark testId={`${testId}-in-use`} />}
      </button>
      {open && (
        <div id={bodyId} className="cxa__body">
          {lead && <p className="cxa__lead">{lead}</p>}
          {children}
        </div>
      )}
    </div>
  );
}

export function ChevronGlyph() {
  return (
    <svg className="cxa__chevron" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor"
         strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M9 6l6 6-6 6" />
    </svg>
  );
}
